import React, { useState, useEffect } from 'react';
import { FiShield } from 'react-icons/fi';
import Footer from './Footer';
import './LandingPage.css';

interface LandingPageProps {
  onSignIn: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onSignIn }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className={`landing-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-logo">
          <img src="/logo.png" alt="CaseKaizen Logo" className="logo-img" />
        </div>
        <div className="nav-links">
          <a href="/team">About</a>
          <button className="nav-signin-btn" onClick={onSignIn}>
            Sign In
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
                  <h1>CaseKaizen</h1>
        <h2>Autonomous AI consultation Agent</h2>
        <p>An AI agent that autonomously analyzes consultation forms, generates eligibility assessments, and delivers automated summaries via email—powered by advanced language models</p>
        <button className="hero-cta-button" onClick={onSignIn}>
          Try it Now
        </button>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="container">
          <h2>How It Works</h2>
          <p className="section-subtitle">See how CaseKaizen simplifies intake form analysis and consultation summaries in three simple steps</p>
          
          <div className="video-container">
            {/* Video Embed - Replace with your video URL */}
            {/* For YouTube: Replace VIDEO_ID with your YouTube video ID */}
            {/* For Vimeo: Replace VIDEO_ID with your Vimeo video ID */}
            {/* For direct video file: Use the <video> tag below instead */}
            
            {/* YouTube Embed */}
            <div className="video-wrapper">
              <iframe
                src="https://www.youtube.com/embed/VvywMrJDIBc?rel=0&modestbranding=1"
                title="How CaseKaizen Works"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-embed"
                loading="lazy"
              ></iframe>
            </div>

            {/* Alternative: Direct Video File (uncomment to use) */}
            {/* 
            <div className="video-wrapper">
              <video
                className="video-embed"
                controls
                preload="metadata"
                poster="/video-poster.jpg"
              >
                <source src="/path-to-your-video.mp4" type="video/mp4" />
                <source src="/path-to-your-video.webm" type="video/webm" />
                Your browser does not support the video tag.
              </video>
            </div>
            */}

            {/* Alternative: Vimeo Embed (uncomment to use) */}
            {/* 
            <div className="video-wrapper">
              <iframe
                src="https://player.vimeo.com/video/VIDEO_ID"
                title="How CaseKaizen Works"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="video-embed"
              ></iframe>
            </div>
            */}
          </div>

          {/* Steps */}
          <div className="how-it-works-steps">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Share Your Public Intake QR Code</h3>
              <p>Share your public intake QR code on social media, email, instant messaging, business cards, flyers, and more to reach potential clients easily.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Client Fills the Intake Form</h3>
              <p>Clients scan your QR code and fill out the intake form. Our intelligent forms adapt based on your industry and specific requirements.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Get Consultation Summary in Email</h3>
              <p>Receive a comprehensive consultation summary with eligibility analysis, lead scoring, recommendations, and actionable next steps—all delivered automatically to your email in minutes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section id="about" className="about-section">
        <div className="container">
          <h2>About CaseKaizen</h2>
          <p>CaseKaizen is an autonomous AI consultation agent that autonomously analyzes consultation forms, generates eligibility assessments, and delivers automated summaries via email—powered by advanced language models. Our platform helps professionals across industries—including Legal, Real Estate, Finance, and more—by automating intake form analysis and initial consultations, significantly reducing time and costs for businesses and individuals.</p>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Instant Eligibility Analysis</h3>
              <p>AI-powered assessment across multiple industries: Legal (Immigration, Workers' Compensation, Personal Injury), Real Estate (Buying, Selling, Landlord, Tenant), Finance, and more</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📋</div>
              <h3>Automated Consultation Summaries</h3>
              <p>Generate comprehensive, professional consultation reports with eligibility insights, lead quality scoring, opportunity assessment, and actionable recommendations</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Fast & Accurate</h3>
              <p>Get detailed eligibility analysis and consultation summaries delivered via email in minutes, not days. Qualify leads faster and serve clients better</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="container">
          <h2>What Our Clients Say</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p className="testimonial-text">
                  "CaseKaizen's AI-powered eligibility analysis has transformed our immigration practice. We can now qualify leads in minutes instead of spending hours manually reviewing cases. The detailed assessments help us identify strong candidates immediately and provide better service to our clients."
                </p>
                <div className="testimonial-author">
                  <strong>Arvind Dhull</strong>
                  <span>Dhull Law</span>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p className="testimonial-text">
                  "As a solo immigration practitioner, time is everything. CaseKaizen's automated eligibility analysis saves me 10+ hours per week. The AI accurately assesses Express Entry, Family Sponsorship, and Study Permit cases, allowing me to focus on high-value work instead of initial screenings."
                </p>
                <div className="testimonial-author">
                  <strong>Tapan Malik</strong>
                  <span>Immigration Consultant</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage; 